import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { AlertTriangle, CalendarDays, Loader2, MessageSquare, Phone, Plus, RefreshCw, Send } from 'lucide-react';
import ContactPhoneFix from '../components/collections/ContactPhoneFix';
import ContactGapFix from '../components/collections/ContactGapFix';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';

// Collections Center (10/12, Josh). Cases are opened BY HAND (bounces never open
// one); texting goes to the phone on file on the deal's contact through Aircall;
// the whole conversation lives here. Until the Aircall number + keys are set on
// the API service, sends are DRY RUNS (stored, badge says so, nothing leaves).

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_LABEL = { open: 'Open', in_progress: 'In progress', promised: 'Promised', resolved: 'Resolved', closed: 'Closed' };
// 10/26 (Josh): funding-company "tile blob" — PFS / DIB, from the case context (same resolver as letters / texts)
export const FundingCompanyBlob = ({ entity, className = '' }) => entity?.key ? (
  <span data-testid="case-funding-company" title={`Funded by ${entity.name}`}
    className={`inline-block rounded-md px-2 py-0.5 text-xs font-bold tracking-wide align-middle ${entity.key === 'DIB' ? 'bg-indigo-600 text-white' : 'bg-blue-600 text-white'} ${className}`}>
    {entity.key}
  </span>
) : null;

const STATUS_CLASS = {
  open: 'bg-red-100 text-red-700', in_progress: 'bg-amber-100 text-amber-800', promised: 'bg-blue-100 text-blue-700',
  resolved: 'bg-green-100 text-green-700', closed: 'bg-slate-200 text-slate-700',
};
const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');
const segmentsOf = (t) => { const n = (t || '').length; const gsm = [...(t || '')].every(c => c.charCodeAt(0) < 128); const a = gsm ? 160 : 70, b = gsm ? 153 : 67; return n <= a ? 1 : Math.ceil(n / b); };

export default function Collections() {
  const { getAuthHeaders } = useAuth();
  const [params, setParams] = useSearchParams();
  const [cases, setCases] = useState([]);
  const [configured, setConfigured] = useState(null);   // null = not known yet (no banner until the API answers)
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active');
  const [selectedId, setSelectedId] = useState(params.get('case') || null);
  const [thread, setThread] = useState({ thread: null, messages: [] });
  const [templates, setTemplates] = useState([]);
  const [draft, setDraft] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [suggested, setSuggested] = useState('');   // picked from the last return code (R01/R09/R16 vs R08/R29)
  const [sending, setSending] = useState(false);
  const [openDlg, setOpenDlg] = useState(false);
  const [newDeal, setNewDeal] = useState('');
  const [newNote, setNewNote] = useState('');
  const [newStage, setNewStage] = useState('');   // 10/15: '' (leave) | 'Payment Issues' | 'In-House Collection' — never pre-set
  const [opening, setOpening] = useState(false);
  const [promise, setPromise] = useState('');
  const [note, setNote] = useState('');
  const [newBounces, setNewBounces] = useState({ rows: [], count: 0, days: [] });   // TODAY's posted bounces (NY day)
  const [pastDay, setPastDay] = useState('');                                          // 'YYYY-MM-DD' picked on the calendar
  const [pastBounces, setPastBounces] = useState({ rows: [], count: 0 });
  const [pastOpen, setPastOpen] = useState(false);
  const bottomRef = useRef(null);

  const headers = useMemo(() => ({ headers: getAuthHeaders() }), [getAuthHeaders]);

  const loadCases = useCallback(async () => {
    setLoading(true);
    try {
      const q = filter === 'active' ? '' : `?status=${filter}`;
      const res = await axios.get(`${API}/collections/cases${q}`, headers);
      setCases(res.data.cases || []);
      setConfigured(!!res.data.aircall_configured);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not load cases'); }
    finally { setLoading(false); }
  }, [filter, headers]);

  const loadThread = useCallback(async (id) => {
    if (!id) return;
    try {
      const res = await axios.get(`${API}/collections/cases/${id}/thread`, headers);
      setThread(res.data);
      setSuggested(res.data.suggested_template_id || '');
      await axios.post(`${API}/collections/cases/${id}/seen`, {}, headers);
      setCases(prev => prev.map(c => (c.id === id ? { ...c, new_bounce: false, thread: c.thread ? { ...c.thread, unread_count: 0 } : c.thread } : c)));
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not load conversation'); }
  }, [headers]);

  const loadNewBounces = useCallback(async () => {
    try { const r = await axios.get(`${API}/collections/new-bounces`, headers); setNewBounces(r.data); } catch (e) { /* ignore */ }
  }, [headers]);
  const loadPastBounces = useCallback(async (day) => {
    if (!day) { setPastBounces({ rows: [], count: 0 }); return; }
    try { const r = await axios.get(`${API}/collections/new-bounces?date=${day}`, headers); setPastBounces(r.data); } catch (e) { /* ignore */ }
  }, [headers]);
  useEffect(() => { loadPastBounces(pastDay); }, [pastDay, loadPastBounces]);
  const reviewRow = async (r, reviewed) => {
    try {
      await axios.post(`${API}/collections/new-bounces/review`, { transaction_ids: r.return_ids || [], reviewed }, headers);
      await loadNewBounces(); if (pastDay) await loadPastBounces(pastDay);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  // 10/27 (Josh): the panel is TODAY only — "Mark all reviewed" stamps today's rows, never older days (calendar = history)
  const markReviewed = async () => {
    const ids = (newBounces.rows || []).filter(r => !r.reviewed).flatMap(r => r.return_ids || []);
    if (ids.length === 0) return;
    try { await axios.post(`${API}/collections/new-bounces/review`, { transaction_ids: ids, reviewed: true }, headers); toast.success("Today's bounces marked reviewed"); await loadNewBounces(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  // 10/27 (Josh): "Message all" — open a case for every deal bounced today and text each merchant (template by return code)
  const [msgAllOpen, setMsgAllOpen] = useState(false);
  const [msgAllPlan, setMsgAllPlan] = useState(null);     // dry-run result from the API
  const [msgAllStage, setMsgAllStage] = useState('');
  const [msgAllBusy, setMsgAllBusy] = useState(false);
  // 10/28 (Josh): a second "Message all" in the page header texts every CASE the filter lists (template by the
  // deal's last return code, one text per merchant per day); the bounces-panel one still opens cases for today's bounces.
  const [msgAllScope, setMsgAllScope] = useState('bounces');   // 'bounces' | 'cases'
  const msgAllUrl = (scope) => `${API}/collections/${scope === 'cases' ? 'cases' : 'new-bounces'}/message-all`;
  // 10/29 (Josh, "send the texts to the clients for today" asked late on 10/28): the bounces button can also run for
  // the PAST day picked on the calendar — the API already takes `date`; the dialog says which day it is texting.
  const [msgAllDate, setMsgAllDate] = useState('');            // '' = today
  const openMessageAll = async (scope = 'bounces', date = '') => {
    setMsgAllScope(scope); setMsgAllDate(date); setMsgAllOpen(true); setMsgAllPlan(null); setMsgAllStage('');
    const body = scope === 'cases' ? { dry_run: true, status: filter, ...(date ? { date } : {}) } : { dry_run: true, ...(date ? { date } : {}) };
    try { const r = await axios.post(msgAllUrl(scope), body, headers); setMsgAllPlan(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not plan the send'); setMsgAllOpen(false); }
  };
  const sendMessageAll = async () => {
    setMsgAllBusy(true);
    try {
      const body = msgAllScope === 'cases' ? { dry_run: false, status: filter, ...(msgAllDate ? { date: msgAllDate } : {}) } : { dry_run: false, set_stage: msgAllStage || null, ...(msgAllDate ? { date: msgAllDate } : {}) };
      const r = await axios.post(msgAllUrl(msgAllScope), body, headers);
      const s = r.data.summary || {};
      const sentWord = r.data.aircall_configured ? 'texted' : 'stored as dry run (Aircall not configured)';
      const opened = msgAllScope === 'cases' ? '' : ` · ${s.opened} case${s.opened === 1 ? '' : 's'} opened`;
      toast.success(`${s.sent + s.dry_run} ${sentWord}${opened} · ${s.skipped} skipped${s.errors ? ` · ${s.errors} failed` : ''}`);
      setMsgAllPlan(r.data);
      await loadNewBounces(); await loadCases();
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
    finally { setMsgAllBusy(false); }
  };

  useEffect(() => { loadCases(); loadNewBounces(); }, [loadCases, loadNewBounces]);
  useEffect(() => {
    axios.get(`${API}/collections/templates`, headers).then(r => setTemplates((r.data.templates || []).filter(t => t.active !== false))).catch(() => {});
  }, [headers]);
  useEffect(() => { if (selectedId) loadThread(selectedId); }, [selectedId, loadThread]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [thread.messages]);
  useEffect(() => {
    if (!selectedId) return undefined;
    const t = setInterval(() => loadThread(selectedId), 20000);   // replies arrive by webhook; poll the open thread
    return () => clearInterval(t);
  }, [selectedId, loadThread]);

  const selected = cases.find(c => c.id === selectedId) || null;
  const selectedPromise = selected?.promise_date || '';
  useEffect(() => { setPromise(selectedPromise); }, [selectedId, selectedPromise]);

  const pick = (id) => { setSelectedId(id); setParams(id ? { case: id } : {}); setDraft(''); setTemplateId(''); };

  const applyTemplate = (tid) => {
    setTemplateId(tid);
    const t = templates.find(x => x.id === tid);
    if (!t) return;
    // Render client-side for preview with the case's variables; the server re-renders on send.
    const vars = { ...(selected?.context?.variables || {}), promise_date: selected?.promise_date || '' };
    setDraft(t.body.replace(/\{([a-z_]+)\}/g, (m, k) => (vars[k] ? String(vars[k]) : m)));
  };

  const send = async () => {
    if (!selected || !draft.trim()) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/collections/cases/${selected.id}/messages`, { body: draft, template_id: templateId || null }, headers);
      toast.success(res.data.dry_run ? 'Stored as DRY RUN — Aircall is not configured yet' : 'Text sent');
      setDraft(''); setTemplateId('');
      await loadThread(selected.id); await loadCases();
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
    finally { setSending(false); }
  };

  const patchCase = async (body, okMsg) => {
    if (!selected) return;
    try {
      const res = await axios.patch(`${API}/collections/cases/${selected.id}`, body, headers);
      const r = res.data?.promise_reminder;   // 10/20: promise date ↔ reminder (created / moved / dismissed)
      const suffix = r?.action === 'created' ? ' — reminder set for 9:00 AM that day' : r?.action === 'moved' ? ' — reminder moved' : r?.action === 'dismissed' ? ' — reminder dismissed' : '';
      if (okMsg) toast.success(okMsg + suffix);
      setNote('');
      await loadCases();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };

  const openCase = async () => {
    if (!newDeal.trim()) return;
    setOpening(true);
    try {
      const res = await axios.post(`${API}/collections/cases`, { deal: newDeal.trim(), set_stage: newStage || null, note: newNote || null }, headers);
      const c = res.data.case;
      toast.success(res.data.already_open ? `Case for #${c.deal_id_number} was already open` : `Case opened for #${c.deal_id_number}${res.data.moved_to_payment_issues ? ` · deal moved to ${res.data.moved_to_payment_issues}` : ''}`);
      setOpenDlg(false); setNewDeal(''); setNewNote(''); setNewStage('');
      setFilter('active');
      await loadCases(); await loadNewBounces();
      pick(c.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not open case'); }
    finally { setOpening(false); }
  };

  const ctx = selected?.context || {};
  const optedOut = !!thread.thread?.opted_out;
  const noPhone = selected && !ctx.contact?.phone_e164;

  return (
    <Layout>
      <div className="p-6 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2"><MessageSquare className="h-6 w-6" /> Collections</h1>
            <p className="text-sm text-muted-foreground">Text merchants on the phone on file. Every message stays on the case.</p>
          </div>
          <div className="flex items-center gap-2">
            {configured === false && (
              <Badge variant="outline" className="border-amber-400 text-amber-700 bg-amber-50">
                <AlertTriangle className="h-3 w-3 mr-1" /> Aircall not configured — sends are dry runs
              </Badge>
            )}
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Open + in progress</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in_progress">In progress</SelectItem>
                <SelectItem value="promised">Promised</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => openMessageAll('cases')} disabled={loading || cases.length === 0 || filter === 'closed'}
                    title="Text the cases in this list whose deal bounced in today's upload — the SMS template is picked by each deal's last return code" data-testid="cases-message-all">
              <Send className="h-4 w-4 mr-1" /> Message all
            </Button>
            <Button variant="outline" size="icon" onClick={loadCases} title="Refresh"><RefreshCw className="h-4 w-4" /></Button>
            <Button onClick={() => setOpenDlg(true)}><Plus className="h-4 w-4 mr-1" /> Open case</Button>
          </div>
        </div>

        {/* Josh 10/13: date marker — today's posted bounces on top, past days below via a small calendar */}
        {(() => {
          const BounceRows = ({ rows, empty }) => (
            rows.length === 0
              ? <div className="px-4 py-3 text-sm text-muted-foreground border-t">{empty}</div>
              : rows.map(r => (
                <div key={r.deal_id} className={`flex items-center justify-between px-4 py-2 border-t text-sm gap-2 ${r.reviewed ? 'opacity-60 bg-slate-50' : ''}`}>
                  <div className="min-w-0">
                    <Link to={`/deals/${r.deal_id}`} className="font-medium text-blue-700 hover:underline">#{r.deal_id_number}</Link>
                    <span className="ml-2 truncate">{r.deal_name}</span>
                    <span className="ml-2 text-xs text-muted-foreground">{r.count} return{r.count === 1 ? '' : 's'} · ${r.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })} · bank date {r.last_date} · {r.status}</span>
                    {(r.reasons || []).length > 0 && <div className={`text-xs mt-0.5 ${r.reviewed ? 'text-slate-500' : 'text-red-700'}`}>{r.reasons.join(' · ')}</div>}
                    {/* 10/13: contact gaps up front — the auto outreach texts the phone and emails the email on file */}
                    {(r.no_contact || !r.contact_phone_ok || !r.contact_email_ok) && (
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        {r.no_contact
                          ? <span className="text-[11px] font-medium text-red-700">no merchant contact linked to this deal — link one on the deal page</span>
                          : (<>
                            {!r.contact_phone_ok && <ContactGapFix dealId={r.deal_id} field="phone" contactName={r.contact_name} headers={headers}
                                                                   onSaved={async () => { await loadNewBounces(); if (pastDay) await loadPastBounces(pastDay); }} />}
                            {!r.contact_email_ok && <ContactGapFix dealId={r.deal_id} field="email" contactName={r.contact_name} headers={headers}
                                                                   onSaved={async () => { await loadNewBounces(); if (pastDay) await loadPastBounces(pastDay); }} />}
                          </>)}
                      </div>
                    )}
                    {r.reviewed && <div className="text-[11px] text-emerald-700 mt-0.5">✓ Reviewed {r.reviewed_at ? fmtWhen(r.reviewed_at) : ''}{r.reviewed_by ? ` · ${r.reviewed_by}` : ''}</div>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {r.reviewed
                      ? <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => reviewRow(r, false)}>Undo</Button>
                      : <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => reviewRow(r, true)}>Reviewed ✓</Button>}
                    {r.case_id
                      ? <Button size="sm" variant="outline" className="h-8" onClick={() => pick(r.case_id)}>View case</Button>
                      : <Button size="sm" className="h-8" onClick={() => { setNewDeal(r.deal_id_number); setOpenDlg(true); }}>Open case</Button>}
                  </div>
                </div>
              ))
          );
          const todayLabel = newBounces.today ? new Date(`${newBounces.today}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : 'today';
          const pastLabel = pastDay ? new Date(`${pastDay}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : '';
          const markedDays = (newBounces.days || []).filter(d => d !== newBounces.today).map(d => new Date(`${d}T12:00:00`));
          return (
            <Card className={newBounces.count > 0 ? 'border-red-200' : ''}>
              <CardHeader className="py-3 flex flex-row items-center justify-between space-y-0">
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertTriangle className={`h-4 w-4 ${newBounces.count > 0 ? 'text-red-600' : 'text-slate-400'}`} /> New bounces — {todayLabel} ({newBounces.count})
                  <span className="text-xs font-normal text-muted-foreground">posted to the CRM today · previous days: pick a day below</span>
                </CardTitle>
                <div className="flex items-center gap-2">
                  {newBounces.rows.some(r => !r.reviewed) && <Button variant="outline" size="sm" onClick={markReviewed}>Mark all reviewed</Button>}
                  {newBounces.count > 0 && <Button size="sm" onClick={() => openMessageAll('bounces')}><Send className="h-4 w-4 mr-1" /> Message all</Button>}
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="max-h-64 overflow-y-auto"><BounceRows rows={newBounces.rows} empty="No bounces posted today." /></div>
                <div className="border-t bg-slate-50 px-4 py-2 flex items-center gap-3 text-sm">
                  <span className="font-medium text-slate-700">Past bounces</span>
                  <Popover open={pastOpen} onOpenChange={setPastOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" className="h-8"><CalendarDays className="h-3.5 w-3.5 mr-1" />{pastLabel || 'Pick a day'}</Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={pastDay ? new Date(`${pastDay}T12:00:00`) : undefined}
                                onSelect={(d) => { if (d) { const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, '0'), dd = String(d.getDate()).padStart(2, '0'); setPastDay(`${y}-${m}-${dd}`); } setPastOpen(false); }}
                                modifiers={{ bounced: markedDays }} modifiersClassNames={{ bounced: 'font-bold text-red-700 underline decoration-red-400' }}
                                disabled={{ after: new Date() }} />
                      <div className="px-3 pb-2 text-[11px] text-muted-foreground">Days in red had bounces posted (last 120 days).</div>
                    </PopoverContent>
                  </Popover>
                  {pastDay && <button className="text-xs text-blue-700 hover:underline" onClick={() => setPastDay('')}>clear</button>}
                  {pastDay && <span className="text-xs text-muted-foreground ml-auto">{pastBounces.count} deal{pastBounces.count === 1 ? '' : 's'}</span>}
                  {pastDay && pastBounces.count > 0 && <Button size="sm" className="h-8" onClick={() => openMessageAll('bounces', pastDay)} data-testid="past-message-all"><Send className="h-4 w-4 mr-1" /> Message all ({pastLabel})</Button>}
                </div>
                {pastDay && <div className="max-h-64 overflow-y-auto"><BounceRows rows={pastBounces.rows} empty={`No bounces were posted on ${pastLabel}.`} /></div>}
              </CardContent>
            </Card>
          );
        })()}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* case list */}
          <Card className="lg:col-span-1">
            <CardHeader className="py-3"><CardTitle className="text-base">Cases ({cases.length})</CardTitle></CardHeader>
            <CardContent className="p-0 max-h-[70vh] overflow-y-auto">
              {loading ? <div className="p-6 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
                : cases.length === 0 ? <div className="p-6 text-sm text-muted-foreground text-center">No cases. Open one with the button above.</div>
                : cases.map(c => {
                  const unread = c.thread?.unread_count || 0;
                  return (
                    <button key={c.id} onClick={() => pick(c.id)}
                      className={`w-full text-left px-4 py-3 border-b hover:bg-slate-50 ${c.id === selectedId ? 'bg-blue-50' : ''}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-medium truncate">#{c.deal_id_number} · {c.context?.deal?.deal_name || c.context?.contact?.company_name || '—'}</div>
                        <div className="flex items-center gap-1 shrink-0">
                          <FundingCompanyBlob entity={c.context?.entity} />
                          {c.new_bounce && <Badge className="bg-red-600 text-white">new bounce</Badge>}
                          {unread > 0 && <Badge className="bg-blue-600 text-white">{unread}</Badge>}
                          <Badge className={STATUS_CLASS[c.status] || ''}>{STATUS_LABEL[c.status] || c.status}</Badge>
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1 flex justify-between">
                        <span>Balance {c.context?.deal?.balance || '—'} · {c.context?.bounces?.count || 0} bounce{c.context?.bounces?.count === 1 ? '' : 's'}</span>
                        <span>{fmtWhen(c.last_activity_at)}</span>
                      </div>
                    </button>
                  );
                })}
            </CardContent>
          </Card>

          {/* conversation */}
          <Card className="lg:col-span-2">
            {!selected ? (
              <CardContent className="p-10 text-center text-muted-foreground">Pick a case to see the conversation.</CardContent>
            ) : (
              <>
                <CardHeader className="py-3 border-b">
                  <div className="flex items-start justify-between flex-wrap gap-2">
                    <div>
                      <CardTitle className="text-base">
                        <Link to={`/deals/${selected.deal_id}`} className="text-blue-700 hover:underline">#{selected.deal_id_number} {ctx.deal?.deal_name}</Link>
                        <span className="ml-2 text-xs font-normal text-muted-foreground">{ctx.deal?.status}</span>
                        <FundingCompanyBlob entity={ctx.entity} className="ml-2" />
                      </CardTitle>
                      <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-4">
                        {/* key = case id: click-to-dial browser extensions (Aircall) replace this text node with their own
                            element, so a plain re-render never reaches it — remount it for every case (Josh 10/13). */}
                        <span key={`phone-${selected.id}`} className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />
                          {ctx.contact?.phone_e164 ? `${ctx.contact.phone_raw} (${ctx.contact.name || ctx.contact.company_name || 'contact'})` : <span className="text-red-600">no usable phone on the contact</span>}
                        </span>
                        <span>Balance {ctx.deal?.balance}</span>
                        {ctx.bounces?.last_date && <span>Last bounce {ctx.bounces.last_amount} on {ctx.bounces.last_date} {ctx.bounces.last_code && `(${ctx.bounces.last_code})`}</span>}
                        <span>Texts as {ctx.entity?.name}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Select value={selected.status} onValueChange={(v) => patchCase({ status: v }, `Status → ${STATUS_LABEL[v]}`)}>
                        <SelectTrigger className="w-36 h-8"><SelectValue /></SelectTrigger>
                        <SelectContent>{Object.keys(STATUS_LABEL).map(s => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}</SelectContent>
                      </Select>
                      <Input type="date" className="h-8 w-40" value={promise} onChange={e => setPromise(e.target.value)}
                        onBlur={() => { if (promise !== (selected.promise_date || '')) patchCase({ promise_date: promise, status: promise ? 'promised' : undefined }, promise ? 'Promise date saved' : 'Promise cleared'); }}
                        title="Promise-to-pay date" />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="h-[46vh] overflow-y-auto p-4 space-y-2 bg-slate-50">
                    {thread.messages.length === 0 && <div className="text-sm text-muted-foreground text-center mt-10">No messages yet.</div>}
                    {thread.messages.map(m => (
                      <div key={m.id} className={`flex ${m.direction === 'out' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm shadow-sm ${m.direction === 'out' ? 'bg-blue-600 text-white' : 'bg-white border'}`}>
                          <div className="whitespace-pre-wrap">{m.body}</div>
                          <div className={`text-[10px] mt-1 ${m.direction === 'out' ? 'text-blue-100' : 'text-muted-foreground'}`}>
                            {fmtWhen(m.created_at)}{m.direction === 'out' && ` · ${m.sent_by_email || ''} · ${m.status}`}{m.error && <span className="text-red-200"> · {m.error}</span>}
                          </div>
                        </div>
                      </div>
                    ))}
                    <div ref={bottomRef} />
                  </div>
                  <div className="border-t p-3 space-y-2">
                    {ctx.deal?.legal_stage && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2" data-testid="legal-stage-notice">
          This deal is in {ctx.deal?.status === 'Default' ? 'Default' : 'recovery'}{ctx.deal?.recovery_status ? ` (${ctx.deal.recovery_status})` : ''}. Automatic outreach never texts or emails it. Contacting the merchant directly may not be permitted (e.g. represented by counsel) — confirm before sending.
        </div>}
                    {optedOut && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">This number replied STOP. Texting is blocked.</div>}
                    {noPhone && <ContactPhoneFix dealId={selected.deal_id} contactName={ctx.contact?.name || ctx.contact?.company_name}
                                                 headers={headers} onSaved={async () => { await loadCases(); await loadThread(selected.id); }} />}
                    <div className="flex gap-2">
                      <Select value={templateId} onValueChange={applyTemplate}>
                        <SelectTrigger className="w-64 h-9"><SelectValue placeholder="Template…" /></SelectTrigger>
                        <SelectContent>{templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}{t.id === suggested ? ' ★' : ''}</SelectItem>)}</SelectContent>
                      </Select>
                      {suggested && !draft && <Button variant="outline" className="h-9" onClick={() => applyTemplate(suggested)}>Use suggested</Button>}
                      <span className="text-xs text-muted-foreground self-center ml-auto">{draft.length} chars · {segmentsOf(draft)} segment{segmentsOf(draft) === 1 ? '' : 's'}</span>
                    </div>
                    <Textarea rows={3} value={draft} onChange={e => setDraft(e.target.value)} placeholder="Type a text…" disabled={optedOut || noPhone} />
                    <div className="flex justify-between items-center gap-2">
                      <div className="flex gap-2 flex-1">
                        <Input className="h-9" placeholder="Internal note (not sent)" value={note} onChange={e => setNote(e.target.value)} />
                        <Button variant="outline" className="h-9" disabled={!note.trim()} onClick={() => patchCase({ note }, 'Note added')}>Add note</Button>
                      </div>
                      <Button onClick={send} disabled={sending || optedOut || noPhone || !draft.trim()}>
                        {sending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}{configured === false ? 'Send (dry run)' : 'Send'}
                      </Button>
                    </div>
                    {(selected.notes || []).length > 0 && (
                      <div className="text-xs text-muted-foreground border-t pt-2 space-y-1">
                        {selected.notes.slice(-5).map((n, i) => <div key={i}><span className="font-medium">{fmtWhen(n.at)}</span> {n.by && `· ${n.by} `}— {n.text}</div>)}
                      </div>
                    )}
                  </div>
                </CardContent>
              </>
            )}
          </Card>
        </div>
      </div>

      <Dialog open={openDlg} onOpenChange={setOpenDlg}>
        <DialogContent>
          <DialogHeader><DialogTitle>Open a collections case</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium">Deal number</label>
              <Input value={newDeal} onChange={e => setNewDeal(e.target.value)} placeholder="e.g. 10719" autoFocus onKeyDown={e => e.key === 'Enter' && openCase()} />
            </div>
            {/* 10/15 Josh: no "Mark Default" from this dialog — Default is set only from the deal page; Payment Issues instead */}
            <div className="space-y-1 text-sm">
              <div className="font-medium">Deal stage</div>
              {[['', 'Leave as is'], ['Payment Issues', 'Move to Payment Issues'], ['In-House Collection', 'Move to In-House Collection (no default fee)']].map(([v, label]) => (
                <label key={v || 'none'} className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="open-case-stage" checked={newStage === v} onChange={() => setNewStage(v)} />
                  {label}
                </label>
              ))}
              <div className="text-[11px] text-muted-foreground">Only a Funded / Payment Issues / In-House Collection deal moves. Default is set from the deal page only.</div>
            </div>
            <div>
              <label className="text-sm font-medium">Note (optional)</label>
              <Textarea rows={2} value={newNote} onChange={e => setNewNote(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDlg(false)}>Cancel</Button>
            <Button onClick={openCase} disabled={opening || !newDeal.trim()}>{opening && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Open case</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* 10/27 (Josh): Message all — plan first (who gets texted, who is skipped and why), then send */}
      <Dialog open={msgAllOpen} onOpenChange={(o) => { if (!msgAllBusy) setMsgAllOpen(o); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{msgAllScope === 'cases' ? `Message the cases in that day's upload${msgAllPlan ? ` (${(msgAllPlan.results || []).length})` : ''}` : msgAllDate ? `Message every merchant bounced on ${new Date(`${msgAllDate}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}` : 'Message every merchant bounced today'}</DialogTitle></DialogHeader>
          {!msgAllPlan ? <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Planning…</div> : (
            <div className="space-y-3 text-sm">
              {!msgAllPlan.aircall_configured && <div className="rounded border border-amber-300 bg-amber-50 text-amber-800 px-3 py-2 text-xs"><AlertTriangle className="h-3 w-3 inline mr-1" />Aircall is not configured — texts are stored as dry runs, nothing leaves.</div>}
              <div className="text-xs text-muted-foreground">
                {msgAllScope === 'cases'
                  ? `Only cases whose deal had a bounce POSTED ${msgAllPlan.date ? `on ${new Date(`${msgAllPlan.date}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}` : 'today'} (that day's upload) are listed and texted — ONE text each with the SMS template for the deal's last return code (R01 / R09 / R16 vs R08 / R29). Default / legal deals, missing phones, STOP numbers and cases already texted today are skipped — nothing is invented.${msgAllPlan.not_in_upload ? ` ${msgAllPlan.not_in_upload} older case${msgAllPlan.not_in_upload === 1 ? '' : 's'} with no bounce that day ${msgAllPlan.not_in_upload === 1 ? 'is' : 'are'} left out.` : ''}`
                  : 'Each deal gets a case (an open case is reused) and ONE text with the SMS template for its last return code. Default / legal deals, missing phones and STOP numbers are skipped — nothing is invented.'}
              </div>
              <div className="max-h-72 overflow-y-auto border rounded divide-y">
                {(msgAllPlan.results || []).map(r => (
                  <div key={r.case_id || r.deal_id} className="flex items-start justify-between gap-2 px-3 py-1.5">
                    <div className="min-w-0">
                      <Link to={`/deals/${r.deal_id}`} className="font-medium text-blue-700 hover:underline">#{r.deal_id_number}</Link>
                      <span className="ml-2">{r.deal_name}</span>
                      <span className="ml-2 text-xs text-muted-foreground">{(r.codes || []).join(', ')}</span>
                    </div>
                    <div className="text-xs shrink-0 text-right">
                      {r.action === 'skip'
                        ? <span className="text-amber-700">skipped — {r.reason}</span>
                        : r.result
                          ? <span className={r.result === 'error' ? 'text-red-700' : 'text-emerald-700'}>{r.result === 'sent' ? 'sent' : r.result === 'dry_run' ? 'stored (dry run)' : `failed — ${r.error || ''}`}{r.case_opened ? ' · case opened' : ''}{r.moved_to ? ` · → ${r.moved_to}` : ''}</span>
                          : <span className="text-slate-700">{r.template_name}{msgAllScope === 'cases' ? '' : ` · ${r.case === 'existing' ? 'existing case' : 'opens a case'}`}</span>}
                    </div>
                  </div>
                ))}
                {(msgAllPlan.results || []).length === 0 && <div className="px-3 py-2 text-muted-foreground">{msgAllScope === 'cases' ? 'No case in the list had a bounce posted that day.' : msgAllDate ? 'No bounces were posted that day.' : 'No bounces posted today.'}</div>}
              </div>
              {!msgAllPlan.dry_run || msgAllScope === 'cases' ? null : (
                <div className="space-y-1">
                  <div className="font-medium">Deal stage for the cases this opens</div>
                  {[['', 'Leave as is'], ['Payment Issues', 'Move to Payment Issues'], ['In-House Collection', 'Move to In-House Collection (no default fee)']].map(([v, label]) => (
                    <label key={v || 'none'} className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="msg-all-stage" checked={msgAllStage === v} onChange={() => setMsgAllStage(v)} />{label}
                    </label>
                  ))}
                  <div className="text-[11px] text-muted-foreground">Only a Funded / Payment Issues / In-House Collection deal moves; deals that already have an open case keep their stage.</div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMsgAllOpen(false)} disabled={msgAllBusy}>{msgAllPlan && !msgAllPlan.dry_run ? 'Close' : 'Cancel'}</Button>
            {msgAllPlan?.dry_run && (
              <Button onClick={sendMessageAll} disabled={msgAllBusy || (msgAllPlan.summary?.text || 0) === 0}>
                {msgAllBusy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
                {msgAllScope === 'cases' ? 'Text' : 'Open cases + text'} {msgAllPlan.summary?.text || 0} merchant{(msgAllPlan.summary?.text || 0) === 1 ? '' : 's'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
